export interface SupplierContact {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface SupplierContactFormValue {
  name: string;
  phone: string;
  email: string;
}

function normalizeOptionalText(value?: string | null): string | null {
  if (value === undefined || value === null) {
    return null;
  }

  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
}

function normalizeSupplierContact(contact?: SupplierContact | null): SupplierContact | null {
  if (!contact) {
    return null;
  }

  const name = normalizeOptionalText(contact.name);
  const phone = normalizeOptionalText(contact.phone);
  const email = normalizeOptionalText(contact.email);

  if (!name && !phone && !email) {
    return null;
  }

  return {
    ...(name ? { name } : {}),
    ...(phone ? { phone } : {}),
    ...(email ? { email } : {})
  };
}

export function normalizeSupplierContacts(
  contactPeople?: SupplierContact[] | SupplierContactFormValue[] | null,
  fallbackContactPerson?: string | null,
  fallbackPhone?: string | null,
  fallbackEmail?: string | null
): SupplierContact[] {
  const normalizedContacts = Array.isArray(contactPeople)
    ? contactPeople.map(normalizeSupplierContact).filter(Boolean) as SupplierContact[]
    : [];

  if (normalizedContacts.length > 0) {
    return normalizedContacts;
  }

  const fallbackContact = normalizeSupplierContact({
    name: fallbackContactPerson,
    phone: fallbackPhone,
    email: fallbackEmail
  });

  return fallbackContact ? [fallbackContact] : [];
}

export function getPrimarySupplierContact(
  contactPeople?: SupplierContact[] | null,
  fallbackContactPerson?: string | null,
  fallbackPhone?: string | null,
  fallbackEmail?: string | null
): SupplierContact | null {
  const normalizedContacts = normalizeSupplierContacts(
    contactPeople,
    fallbackContactPerson,
    fallbackPhone,
    fallbackEmail
  );

  return normalizedContacts[0] || null;
}

export function getSupplierContactsSearchText(
  contactPeople?: SupplierContact[] | null,
  fallbackContactPerson?: string | null,
  fallbackPhone?: string | null,
  fallbackEmail?: string | null
): string {
  return normalizeSupplierContacts(contactPeople, fallbackContactPerson, fallbackPhone, fallbackEmail)
    .flatMap((contact) => [contact.name, contact.phone, contact.email])
    .filter(Boolean)
    .join(' ');
}

export function toSupplierContactFormList(
  contactPeople?: SupplierContact[] | null,
  fallbackContactPerson?: string | null,
  fallbackPhone?: string | null,
  fallbackEmail?: string | null
): SupplierContactFormValue[] {
  const normalizedContacts = normalizeSupplierContacts(
    contactPeople,
    fallbackContactPerson,
    fallbackPhone,
    fallbackEmail
  );

  if (normalizedContacts.length === 0) {
    return [{ name: '', phone: '', email: '' }];
  }

  return normalizedContacts.map((contact) => ({
    name: contact.name || '',
    phone: contact.phone || '',
    email: contact.email || ''
  }));
}
